import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column, ErrorText, Row } from "@/components";
import { AutosaveFormDateInput } from "@/components/companies/shared/autosave-form/AutosaveFormDateInput";
import { AutosaveFormRichTextInput } from "@/components/companies/shared/autosave-form/AutosaveFormRichTextInput";
import { IntakeForm } from "@/components/companies/shared/IntakeForm";
import { IntakeGuidance } from "@/components/companies/shared/IntakeGuidance";
import { ReadOnlyField, ReadOnlyRichTextField } from "@/components/companies/shared/ReadOnlyField";

import { useUpdateSetJobIntake } from "@/hooks/updateSets/useUpdateSetJobIntake";
import { dates } from "@/lib/dates";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { JobIntake, JobIntakeDatesAndUrgency } from "@/types";

const datesSchema = z.object({
  deadlineFirstBatch: z.string().nullable(),
  deadlineFirstPlacement: z.string().nullable(),
  deadlineJobFinished: z.string().nullable(),

  assumptions: z.string().nullable(),
  details: z.string().nullable(),
});

export const DatesAndUrgency = ({
  isEditing,
  jobIntake,
}: {
  isEditing: boolean;
  jobIntake: JobIntake;
}) => {
  const { mutate: updateJobIntake, isError } = useUpdateSetJobIntake();

  const handleSave = (data: JobIntakeDatesAndUrgency) => {
    mixpanelUtils.trackFormSubmission("Dates and urgency", MIXPANEL_SCREENS.JOB);
    updateJobIntake({ updateJobIntakeRequest: { datesAndUrgency: data } });
  };

  return (
    <Column className="gap-4">
      {isEditing ? (
        <DatesForm
          datesAndUrgency={jobIntake.datesAndUrgency}
          onSave={handleSave}
        />
      ) : (
        <DatesReadOnly datesAndUrgency={jobIntake.datesAndUrgency} />
      )}
      {isError && (
        <ErrorText errorMessage="An error occurred while saving. Please try again." />
      )}
    </Column>
  );
};

const DatesForm = ({
  datesAndUrgency,
  onSave,
}: {
  datesAndUrgency: JobIntakeDatesAndUrgency;
  onSave: (data: JobIntakeDatesAndUrgency) => void;
}) => {
  const form = useForm<z.infer<typeof datesSchema>>({
    resolver: zodResolver(datesSchema),
    defaultValues: datesAndUrgency,
  });

  const saveOnBlur = () => {
    void form.handleSubmit(onSave)();
  };

  return (
    <IntakeForm form={form} onSave={saveOnBlur}>
      <Column className="gap-6">
        <Row className="gap-4">
          <AutosaveFormDateInput
            className="flex-1"
            fieldName="deadlineFirstBatch"
            form={form}
            infoTooltip="This can be a goal or client requested deadline - specify in details below! This value will be specified for a staffer to see!"
            label="Deadline for first batch"
            onBlur={saveOnBlur}
          />
          <AutosaveFormDateInput
            className="flex-1"
            fieldName="deadlineFirstPlacement"
            form={form}
            infoTooltip="This can be a goal or client requested deadline - specify in details below! Time to first placement is crucial with new clients!"
            label="Deadline for first placement"
            onBlur={saveOnBlur}
          />
          <AutosaveFormDateInput
            className="flex-1"
            fieldName="deadlineJobFinished"
            form={form}
            infoTooltip="This can be a goal or client requested deadline - specify in details below! Assumptions are always made on this deadline, so make sure to specify in assumptions below!"
            label="Deadline for job finished"
            onBlur={saveOnBlur}
          />
        </Row>
        <AutosaveFormRichTextInput
          editorHeight="150px"
          fieldName="assumptions"
          form={form}
          infoTooltip={`What are the assumptions made on these deadlines?\nHow many reqs are we assuming need to be filled?\nWhat is the urgency of the client?\nWhat are some of the foreseen challenges we may face: new region, high skill to pay ratio, etc.?`}
          label="Assumptions"
          onBlur={saveOnBlur}
        />
        <AutosaveFormRichTextInput
          editorHeight="150px"
          fieldName="details"
          form={form}
          infoTooltip={`What's driving the deadlines and how important is it that they are met?\nIs the candidate expecting any of these deadlines ie. were they communicated or requested by the client?`}
          label="Details"
          onBlur={saveOnBlur}
        />
        <IntakeGuidance message="It is highly recommended to setup a recurring meeting with new and naughty clients!! This will make everyone's life much easier when trying to move the process along!" />
      </Column>
    </IntakeForm>
  );
};

const DatesReadOnly = ({
  datesAndUrgency,
}: {
  datesAndUrgency: JobIntakeDatesAndUrgency;
}) => {
  const deadlineFirstBatchLabel = datesAndUrgency.deadlineFirstBatch
    ? dates.formatUtcDateForUser(datesAndUrgency.deadlineFirstBatch)
    : null;

  const deadlineFirstPlacementLabel = datesAndUrgency.deadlineFirstPlacement
    ? dates.formatUtcDateForUser(datesAndUrgency.deadlineFirstPlacement)
    : null;

  const deadlineJobFinishedLabel = datesAndUrgency.deadlineJobFinished
    ? dates.formatUtcDateForUser(datesAndUrgency.deadlineJobFinished)
    : null;

  return (
    <Column className="gap-4">
      <Row className="gap-4">
        <ReadOnlyField
          className="flex-1"
          infoTooltip="This can be a goal or client requested deadline - see specifications in details below!"
          label="Deadline for first batch"
          value={deadlineFirstBatchLabel}
        />
        <ReadOnlyField
          className="flex-1"
          infoTooltip="This can be a goal or client requested deadline - see specifications in details below! Time to first placement is crucial with new clients!"
          label="Deadline for first placement"
          value={deadlineFirstPlacementLabel}
        />
        <ReadOnlyField
          className="flex-1"
          infoTooltip="This can be a goal or client requested deadline - see specifications in details below! Additionally, see assumptions made on this deadline in the assumptions section below."
          label="Deadline for job finished"
          value={deadlineJobFinishedLabel}
        />
      </Row>
      <ReadOnlyRichTextField
        label="Assumptions"
        value={datesAndUrgency.assumptions ?? null}
      />
      <ReadOnlyRichTextField
        label="Details"
        value={datesAndUrgency.details ?? null}
      />
      <IntakeGuidance message="It is highly recommended to setup a recurring meeting with new and naughty clients!! This will make everyone's life much easier when trying to move the process along!" />
    </Column>
  );
};
